import { useEffect, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable, type DataTableColumn } from '../../../../shared/components/DataTable.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { billRefLabel } from '../../../../shared/contracts/bill-ref.js';

const BADGE: Record<string, React.CSSProperties> = {
  APPROVED: { background: 'var(--color-success-bg)', color: 'var(--color-success)', padding: '2px 8px', borderRadius: 12 },
  REJECTED: { background: 'var(--color-danger-bg)', color: 'var(--color-danger)', padding: '2px 8px', borderRadius: 12 },
  RETURNED: { background: 'var(--color-warning-bg)', color: 'var(--color-warning)', padding: '2px 8px', borderRadius: 12 },
  IN_PROGRESS: { background: 'var(--color-info-bg)', color: 'var(--color-text-link)', padding: '2px 8px', borderRadius: 12 },
  PENDING: { background: 'var(--color-bg-muted)', color: 'var(--color-text-muted)', padding: '2px 8px', borderRadius: 12 },
  PASS: { background: 'var(--color-success-bg)', color: 'var(--color-success)', padding: '2px 8px', borderRadius: 12 },
  FAIL: { background: 'var(--color-danger-bg)', color: 'var(--color-danger)', padding: '2px 8px', borderRadius: 12 },
  WARNING: { background: 'var(--color-warning-bg)', color: 'var(--color-warning)', padding: '2px 8px', borderRadius: 12 },
};

function StatusBadge({ status }: { status: string }) {
  return <span style={BADGE[status] ?? BADGE.PENDING}>{status}</span>;
}

const tick = (ok: unknown) => (ok ? '✓' : '✗');
/** A claim line has no number of its own: it is the job it bills, at its location. */
const lineLabel = (r: { lineJobNumber?: string | null; lineLocation?: string | null }): string =>
  [r.lineJobNumber, r.lineLocation].filter(Boolean).join(' — ') || '—';

/** A cell whose colour depends on the row, kept out of the column list. */
function Variance({ pct, limit }: { pct: number; limit: number }): ReactNode {
  const { number } = useBusinessNumber();
  return (
    <span style={{ color: pct > limit ? 'var(--color-danger)' : 'var(--color-success)' }}>
      {number(pct)}%
    </span>
  );
}

export function ClaimReviewPage(): ReactNode {
  const { t: tr } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money } = useBusinessNumber();
  // Two different things were being shown by one helper. `invoiceDate` is a
  // stored day and must not be turned into an instant; `reviewedAt`,
  // `certifiedAt` and `createdAt` are instants and must be read in the
  // reader's own zone. They are separated here, and neither spells out a
  // locale any more: both take their arrangement from the tenant's
  // DISPLAY_DATE_FORMAT.
  const { day, at, atDay } = useBusinessDay();
  const { claimId } = useParams<{ claimId: string }>();
  const { token } = useAuth();
  const [data, setData] = useState<any>(null);
  const [tab, setTab] = useState('commercial');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!claimId) return;
    let alive = true;
    apiRequest<any>(`/v1/approval/claim/${claimId}`, { token })
      .then(d => { if (alive) setData(d); })
      .catch(e => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [claimId, token]);

  if (error) return <div style={{ padding: 24, color: 'red' }}>{error}</div>;
  if (!data) return <div style={{ padding: 24 }}>{tr('fa.claim.loading')}</div>;

  const TABS = [
    { key: 'commercial', label: tr('fa.claim.tabCommercial') },
    { key: 'invoice', label: tr('fa.claim.tabInvoice') },
    { key: 'quantity', label: tr('fa.claim.tabQuantity') },
    { key: 'rate', label: tr('fa.claim.tabRate') },
    { key: 'tax', label: tr('fa.claim.tabTax') },
    { key: 'documents', label: tr('fa.claim.tabDocuments') },
    { key: 'duplicate', label: tr('fa.claim.tabDuplicate') },
    { key: 'accounts', label: tr('fa.claim.tabAccounts') },
    { key: 'finance', label: tr('fa.claim.tabFinance') },
    { key: 'certification', label: tr('fa.claim.tabCertification') },
    { key: 'eligibility', label: tr('fa.claim.tabEligibility') },
    { key: 'audit', label: tr('fa.claim.tabAudit') },
  ];

  type Row = Record<string, any>;
  const col = (
    key: string,
    header: string,
    render: (r: Row) => ReactNode,
  ): DataTableColumn<Row> => ({ key, header, render });

  const TABLES: Record<string, {
    rows: readonly Row[];
    rowKey: (r: Row) => string;
    columns: ReadonlyArray<DataTableColumn<Row>>;
  }> = {
    commercial: {
      rows: data.commercialReviews ?? [],
      rowKey: (r) => String(r.reviewId),
      columns: [
        col('status', tr('fa.claim.hdr.status'), (r) => <StatusBadge status={r.reviewStatus} />),
        col('workOk', tr('fa.claim.hdr.workOk'), (r) => tick(r.workCompletionOk)),
        col('areaOk', tr('fa.claim.hdr.areaOk'), (r) => tick(r.areaValidationOk)),
        col('rateOk', tr('fa.claim.hdr.rateOk'), (r) => tick(r.rateComplianceOk)),
        col('docsOk', tr('fa.claim.hdr.docsOk'), (r) => tick(r.documentCompletenessOk)),
        col('decision', tr('fa.claim.hdr.decision'), (r) => r.decision ?? '—'),
        col('date', tr('fa.claim.hdr.date'), (r) => atDay(r.reviewedAt)),
      ],
    },
    invoice: {
      rows: data.invoiceValidations ?? [],
      rowKey: (r) => String(r.validationId),
      columns: [
        col('invoiceNo', tr('fa.claim.hdr.invoiceNo'), (r) => r.invoiceNumber),
        col('date', tr('fa.claim.hdr.date'), (r) => day(r.invoiceDate)),
        col('gst', tr('fa.claim.hdr.gst'), (r) => r.gstNumber ?? '—'),
        col('amount', tr('fa.claim.hdr.amount'), (r) => money(r.invoiceAmount)),
        col('tax', tr('fa.claim.hdr.tax'), (r) => money(r.taxAmount)),
        col('total', tr('fa.claim.hdr.total'), (r) => money(r.totalAmount)),
        col('result', tr('fa.claim.hdr.result'), (r) => <StatusBadge status={r.validationResult} />),
      ],
    },
    quantity: {
      rows: data.quantityValidations ?? [],
      rowKey: (r) => String(r.validationId),
      columns: [
        col('line', tr('fa.claim.hdr.line'), lineLabel),
        col('approved', tr('fa.claim.hdr.approved'), (r) => r.approvedArea),
        col('executed', tr('fa.claim.hdr.executed'), (r) => r.executedArea),
        col('billable', tr('fa.claim.hdr.billable'), (r) => r.billableArea),
        col('claimed', tr('fa.claim.hdr.claimed'), (r) => r.claimedArea),
        col('variancePct', tr('fa.claim.hdr.variancePct'), (r) => <Variance pct={r.variancePct} limit={5} />),
        col('exception', tr('fa.claim.hdr.exception'), (r) => r.exceptionGenerated ? '⚠' : '—'),
        col('result', tr('fa.claim.hdr.result'), (r) => <StatusBadge status={r.validationResult} />),
      ],
    },
    rate: {
      rows: data.rateValidations ?? [],
      rowKey: (r) => String(r.validationId),
      columns: [
        col('line', tr('fa.claim.hdr.line'), lineLabel),
        col('masterRate', tr('fa.claim.hdr.masterRate'), (r) => r.rateMasterRate),
        col('contract', tr('fa.claim.hdr.contract'), (r) => r.contractRate),
        col('applied', tr('fa.claim.hdr.applied'), (r) => r.appliedRate),
        col('source', tr('fa.claim.hdr.source'), (r) => r.rateSource),
        col('mismatchPct', tr('fa.claim.hdr.mismatchPct'), (r) => <Variance pct={r.mismatchPct} limit={2} />),
        col('result', tr('fa.claim.hdr.result'), (r) => <StatusBadge status={r.validationResult} />),
      ],
    },
    tax: {
      rows: data.taxValidations ?? [],
      rowKey: (r) => String(r.validationId),
      columns: [
        col('cgst', tr('fa.claim.hdr.cgst'), (r) => r.cgstAmount),
        col('sgst', tr('fa.claim.hdr.sgst'), (r) => r.sgstAmount),
        col('igst', tr('fa.claim.hdr.igst'), (r) => r.igstAmount),
        col('cess', tr('fa.claim.hdr.cess'), (r) => r.cessAmount),
        col('claimedTotal', tr('fa.claim.hdr.claimedTotal'), (r) => r.claimedTaxTotal),
        col('calculated', tr('fa.claim.hdr.calculated'), (r) => r.calculatedTaxTotal),
        col('variance', tr('fa.claim.hdr.variance'), (r) => r.varianceAmount),
        col('hsnSac', tr('fa.claim.hdr.hsnSac'), (r) => r.hsnSacCode ?? '—'),
        col('result', tr('fa.claim.hdr.result'), (r) => <StatusBadge status={r.validationResult} />),
      ],
    },
    documents: {
      rows: data.documentValidations ?? [],
      rowKey: (r) => String(r.documentTypeCode),
      columns: [
        col('documentType', tr('fa.claim.hdr.documentType'), (r) => r.documentTypeCode),
        col('mandatory', tr('fa.claim.hdr.mandatory'), (r) => r.isMandatory ? 'Yes' : 'No'),
        col('present', tr('fa.claim.hdr.present'), (r) => tick(r.isPresent)),
        col('result', tr('fa.claim.hdr.result'), (r) => <StatusBadge status={r.validationResult} />),
      ],
    },
    duplicate: {
      rows: data.duplicateChecks ?? [],
      rowKey: (r) => String(r.checkId),
      columns: [
        col('invoiceNo', tr('fa.claim.hdr.invoiceNo'), (r) => r.invoiceNumber ?? '—'),
        col('vendor', tr('fa.claim.hdr.vendor'), (r) => personLabel(r.vendorName, r.vendorCode) ?? '—'),
        col('amount', tr('fa.claim.hdr.amount'), (r) => money(r.claimAmount)),
        col('result', tr('fa.claim.hdr.result'), (r) => <StatusBadge status={r.checkResult === 'NO_DUPLICATE' ? 'PASS' : 'FAIL'} />),
        col('confidence', tr('fa.claim.hdr.confidence'), (r) => `${r.confidenceScore ?? '—'}%`),
        col('action', tr('fa.claim.hdr.action'), (r) => r.actionTaken ?? '—'),
      ],
    },
    accounts: {
      rows: data.accountsReviews ?? [],
      rowKey: (r) => String(r.reviewId),
      columns: [
        col('status', tr('fa.claim.hdr.status'), (r) => <StatusBadge status={r.reviewStatus} />),
        col('invoiceOk', tr('fa.claim.hdr.invoiceOk'), (r) => tick(r.invoiceComplianceOk)),
        col('taxOk', tr('fa.claim.hdr.taxOk'), (r) => tick(r.taxComplianceOk)),
        col('docsOk', tr('fa.claim.hdr.docsOk'), (r) => tick(r.supportingDocsOk)),
        col('calcOk', tr('fa.claim.hdr.calcOk'), (r) => tick(r.calculationAccuracyOk)),
        col('acctOk', tr('fa.claim.hdr.acctOk'), (r) => tick(r.accountingReadinessOk)),
        col('decision', tr('fa.claim.hdr.decision'), (r) => r.decision ?? '—'),
      ],
    },
    finance: {
      rows: data.financeReviews ?? [],
      rowKey: (r) => String(r.reviewId),
      columns: [
        col('status', tr('fa.claim.hdr.status'), (r) => <StatusBadge status={r.reviewStatus} />),
        col('liabilityOk', tr('fa.claim.hdr.liabilityOk'), (r) => tick(r.financialLiabilityOk)),
        col('budgetOk', tr('fa.claim.hdr.budgetOk'), (r) => tick(r.budgetAvailabilityOk)),
        col('commOk', tr('fa.claim.hdr.commOk'), (r) => tick(r.commercialApprovalOk)),
        col('acctOk', tr('fa.claim.hdr.acctOk'), (r) => tick(r.accountsApprovalOk)),
        col('policyOk', tr('fa.claim.hdr.policyOk'), (r) => tick(r.policyComplianceOk)),
        col('decision', tr('fa.claim.hdr.decision'), (r) => r.decision ?? '—'),
        col('holdReason', tr('fa.claim.hdr.holdReason'), (r) => r.holdReason ?? '—'),
      ],
    },
    certification: {
      rows: data.certifications ?? [],
      rowKey: (r) => String(r.certificationId),
      columns: [
        col('type', tr('fa.claim.hdr.type'), (r) => r.certificateType),
        col('number', tr('fa.claim.hdr.number'), (r) => r.certificateNumber),
        col('amount', tr('fa.claim.hdr.amount'), (r) => money(r.certifiedAmount)),
        col('tax', tr('fa.claim.hdr.tax'), (r) => money(r.certifiedTaxAmount)),
        col('total', tr('fa.claim.hdr.total'), (r) => money(r.certifiedTotalAmount)),
        col('status', tr('fa.claim.hdr.status'), (r) => <StatusBadge status={r.certificateStatus ?? 'ISSUED'} />),
        col('certifiedAt', tr('fa.claim.hdr.certifiedAt'), (r) => atDay(r.certifiedAt)),
      ],
    },
    eligibility: {
      rows: data.eligibilities ?? [],
      rowKey: (r) => String(r.eligibilityId),
      columns: [
        col('status', tr('fa.claim.hdr.status'), (r) => <StatusBadge status={r.eligibilityStatus === 'ELIGIBLE' ? 'APPROVED' : r.eligibilityStatus} />),
        col('amount', tr('fa.claim.hdr.amount'), (r) => money(r.eligibleAmount)),
        col('tax', tr('fa.claim.hdr.tax'), (r) => money(r.eligibleTaxAmount)),
        col('total', tr('fa.claim.hdr.total'), (r) => money(r.eligibleTotal)),
        col('approvedBy', tr('fa.claim.hdr.approvedBy'), (r) => personLabel(r.approvedByName, r.approvedByCode) ?? '—'),
        col('created', tr('fa.claim.hdr.created'), (r) => atDay(r.createdAt)),
      ],
    },
    audit: {
      rows: data.auditTrail ?? [],
      rowKey: (r) => String(r.auditId),
      columns: [
        col('event', tr('fa.claim.hdr.event'), (r) => r.eventCode),
        col('entity', tr('fa.claim.hdr.entity'), (r) => r.entityType),
        col('user', tr('fa.claim.hdr.user'), (r) => personLabel(r.actorName, r.actorCode) ?? '—'),
        col('ip', tr('fa.claim.hdr.ip'), (r) => r.ipAddress),
        col('timestamp', tr('fa.claim.hdr.timestamp'), (r) => r.createdAt ? at(r.createdAt) : '—'),
      ],
    },
  };

  const active = TABLES[tab];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}><Link to="/financial-approval">{tr('fa.claim.backDashboard')}</Link></div>
      <h1>{tr('fa.claim.title', { bill: billRefLabel(data.bill, day) })}</h1>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ padding: '6px 14px', borderRadius: 6, border: tab === t.key ? '2px solid var(--color-text-link)' : '1px solid var(--color-border-default)', background: tab === t.key ? 'var(--color-info-bg)' : 'var(--color-bg-surface)', cursor: 'pointer' }}>
            {t.label}
          </button>
        ))}
      </div>
      {/* Every tab shows one grid of the same shape, so they are described
          rather than repeated. */}
      {active && (
        <DataTable<Record<string, unknown>>
          ariaLabel={`claim-${tab}`}
          testId={`claim-${tab}-table`}
          columns={active.columns}
          rows={active.rows}
          rowKey={active.rowKey}
          emptyMessage={tr('fa.claim.empty')}
        />
      )}
    </div>
  );
}
